
import { ConfigurationTypes } from '../const/ConfigurationTypes';
import ConfigManager from '../managers/ConfigManager';
import ISpineAnimConfig from '../managers/contracts/configurations/spine_anim/ISpineAnimConfig';
import IAnim from '../managers/contracts/animation/IAnim';
import ISpineAnimStateConfig from '../managers/contracts/configurations/spine_anim/ISpineAnimStateConfig';
import IChainingSpineAnimConfig from '../managers/contracts/configurations/spine_anim/IChainingSpineAnimConfig';

export default class SpineAnimation extends PIXI.spine.Spine implements IAnim<ISpineAnimConfig> {

    public animConfig: ISpineAnimConfig;
    
    constructor(animJson: PIXI.spine.core.SkeletonData, name: string, config: ISpineAnimConfig) {
        super(animJson);
        this.animConfig = config;
        if (this.animConfig) {
            if (this.animConfig.states?.length !== this.stateData.skeletonData.animations.length) {
                console.warn(
                    `[SPINE ANIMATION] -> animation <${name}> decelerated states in game config doesn't have the same length as skeleton data animations or missing configuration declaration`,
                    {
                        [`${name} animation game-config`]: this.animConfig.states,
                        [`${name} animation skeleton-data-animations`]: this.stateData.skeletonData.animations.map(data => data.name)
                    }

                );
            }
        } else {
            console.warn(
                `[SPINE ANIMATION] -> animation <${name}> Has not a configuration in SpineAnimationConfig`,
                ConfigManager.getConfig(ConfigurationTypes.SPINE_ANIMATIONS)
            );
        }

        this.visible = this.animConfig.visible;

    }
    public playAsync(animState = this.animConfig.defaultState, timeScale?: number): Promise<boolean> {
        return new Promise<boolean>((res) => {
            let listener = {
                complete: () => {
                    this.state.removeListener(listener);
                    res(true);
                }
            };

            this.state.addListener(listener);
            this.play(animState, false, timeScale);
        })
    }
    public playAsyncChainingStates(animChainingState: string, timeScale?: number): Promise<boolean> {
        return new Promise<boolean>((res) => {
            let listener = {
                complete: () => {
                    this.state.removeListener(listener);
                    res(true);
                }
            };

            this.state.addListener(listener);
            this.playChainingStates(animChainingState, timeScale, false);
        })
    }

    protected onCompleteOnce(callback) {
        let listener = {
            complete: () => {
                this.state.removeListener(listener);
                callback();
            }
        };

        this.state.addListener(listener);
    }

    /**
     * Play spine animation
     * @param animState: state for current animation witch is defined in-game config
     * @param loop: default is get value from config
     * @returns void
     */
    public play(
        animState = this.animConfig.defaultState,
        loop = this.getStateConfig(animState).loop,
        timeScale?: number,
    ): void {
        if (animState && this.state.hasAnimation(animState)) {
            this.clearState();
            if (timeScale) {
                this.state.timeScale = timeScale;
            } else {
                let stateConfig = this.getStateConfig(animState);
                if (stateConfig && stateConfig.timeScale) {
                    this.state.timeScale = stateConfig.timeScale
                } else {
                    this.state.timeScale = 1;
                }
            }
            this.show();
            this.state.setAnimation(0, animState, loop);

        } else {
            if (this.animConfig?.states?.length === 1) {
                this.clearState();
                if (timeScale) {
                    this.state.timeScale = timeScale;
                } else {
                    this.state.timeScale = 1;
                }
                this.show();
                this.state.setAnimation(0, this.animConfig.states[0].name, loop);
            } else {
                console.warn(
                    `[SPINE ANIMATION] -> animation state <${animState}> is missing from <${this.name}> animation! `,
                    {
                        [`${this.animConfig.name} animation states`]: this.animConfig.states,
                    }

                );
            }
        }
    }

    public getStateConfig(state: string): ISpineAnimStateConfig | undefined {
        return this.animConfig.states.find(data => data.name === state);
    }
    public playAndHide(animState = this.animConfig.defaultState,
        timeScale?: number,) {
        this.playAsync(animState, timeScale).then(() => {
            this.stop(true);
        })
    }

    public hasChainingAnim(chainingAnim: string): boolean {
        return Boolean(this.animConfig.chainingStates.find(data => data.name === chainingAnim))
    }

    public clearState() {
        this.state.clearTracks();
        this.skeleton.setToSetupPose();
    }

    /**
     * name
     */
    public playChainingStates(chainingAnim: string, timeScale?: number, loop?: boolean) {
        let chainingAnimConfig = this.getChainingAnimConfig(chainingAnim);
        if (chainingAnimConfig) {
            let index = chainingAnimConfig.states.indexOf(chainingAnimConfig.entryPoint);
            if (index < 0) {
                console.warn(
                    `[SPINE ANIMATION] -> Spine anim <${this.name}> animation state <${chainingAnimConfig.entryPoint}> is missing from <${chainingAnimConfig.states}> animation! `,
                );
            } else {
                this.clearState();
                timeScale = this.getChainingAnimTimeScaleProp(chainingAnimConfig, timeScale);
                loop = this.getChainingAnimLoopProp(loop, chainingAnimConfig);
                chainingAnimConfig.states.forEach((state, i, arr) => {
                    if (loop) {
                        this.state.setAnimation(i, state, false);
                        this.state.timeScale = timeScale;
                        if (i >= (arr.length - 1)) {
                            this.onCompleteOnce(() => {
                                this.playChainingStates(chainingAnim, timeScale, loop)
                            })
                        }
                    }
                    else {
                        if (chainingAnimConfig.hasLoopStateOnComplete) {
                            if (i === (chainingAnimConfig.states.length - 1)) {
                                this.state.setAnimation(i, chainingAnimConfig.loopStateOnComplete, true);
                            } else {
                                this.state.setAnimation(i, state, false);
                            }
                            this.state.timeScale = timeScale;
                        } else if (chainingAnimConfig.hasStaticStateOnComplete) {
                            if (i === (chainingAnimConfig.states.length - 1)) {
                                this.state.setAnimation(i, state, false);
                                this.onCompleteOnce(() => this.showStatic())
                            } else {
                                this.state.setAnimation(i, state, false);
                            }
                            this.state.timeScale = timeScale;
                        } else if (chainingAnimConfig.hideOnComplete) {
                            this.state.setAnimation(i, state, false);
                            this.onCompleteOnce(() => this.stop(true));
                            this.state.timeScale = timeScale;
                        } else {
                            this.state.setAnimation(i, state, false);
                            this.state.timeScale = timeScale;
                        }
                    }
                })

                this.show();
            }
        } else {
            console.warn(
                `[SPINE ANIMATION] -> animation chaining state <${chainingAnim}> is missing from chaining states animation configurations! `,
                {
                    resolve: [
                        "use spine animation configuration file and set chaining animation configuration",
                        "use generateChainingAnimation method on SpineAnimation class"
                    ]
                }
            );
        }
    }

    /**
    name
     */
    public getChainingAnimConfig(chainingAnim: string): IChainingSpineAnimConfig {
        return this.animConfig.chainingStates?.find(data => data.name === chainingAnim)
    }

    protected getChainingAnimLoopProp(loop: boolean, config: IChainingSpineAnimConfig): boolean {
        let result = false;
        if (loop === null || loop === undefined) {
            if (config.loop) {
                result = true;
            }
        } else {
            result = loop;
        }

        return result
    }

    protected getChainingAnimTimeScaleProp(config: IChainingSpineAnimConfig, timeScale?: number): number {
        let result = 1;
        if (!timeScale) {
            if (config?.timeScale) {
                result = config.timeScale;
            }
        } else {
            result = timeScale;
        }

        return result
    }

    public stop(hide = false) {
        this.state.setEmptyAnimation(0, 0);
        if (hide) {
            this.hide();
        }
    }

    public hide() {
        this.visible = false;
    }

    public show() {
        this.visible = true
    }

    public showStatic(staticAnim?: string, stopOnTime?: number) {

        if (staticAnim && this.state.hasAnimation(staticAnim)) {
            let stopTime = 0;
            if (stopOnTime) {
                stopTime = stopOnTime
            } else if (this.animConfig.stopTimeOnStatic) {
                stopTime = this.animConfig.stopTimeOnStatic
            } else {

            }
            this.show();
            this.play(staticAnim, false);
            this.state.tracks[0].animationStart = stopTime;
            this.state.tracks[0].animationEnd = stopTime;

        } else if (this.animConfig.staticState && this.state.hasAnimation(this.animConfig.staticState)) {
            let stopTime = 0;
            if (stopOnTime) {
                stopTime = stopOnTime
            } else if (this.animConfig.stopTimeOnStatic) {
                stopTime = this.animConfig.stopTimeOnStatic
            } else {

            }
            this.show();
            this.play(this.animConfig.staticState, false);
            this.state.tracks[0].animationStart = stopTime;
            this.state.tracks[0].animationEnd = stopTime;
        }
    }
}
